"use server"

import { sdk } from "@lib/config"
import medusaError from "@lib/util/medusa-error"
import { HttpTypes } from "@medusajs/types"
import { revalidateTag } from "next/cache"
import { redirect } from "next/navigation"
import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  removeCartId,
  setCartId,
  removeAuthToken,
} from "./cookies"
import { saveFamilyStoreToAccount } from "./family-stores"
import { getRegion } from "./regions"
import { saveOrderShippingAddressToAccount } from "./customer"
import { getLocale } from "@lib/data/locale-actions"
import {
  clearPendingDiscountCode,
  getPendingDiscountCode,
  setDiscountNotice,
} from "./discount-actions"
import type { DiscountNotice } from "@lib/util/discount-code"
import type { GiftSyncResult } from "@lib/util/cart-gifts"

/**
 * Retrieves a cart by its ID. If no ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to retrieve.
 * @returns The cart object if found, or null if not found.
 */
export async function retrieveCart(cartId?: string, fields?: string) {
  const id = cartId || (await getCartId())
  fields ??=
    "*items, *region, *items.product, *items.variant, *items.thumbnail, *items.metadata, +items.total, *promotions, +shipping_methods.name"

  if (!id) {
    return null
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("carts")),
  }

  return await sdk.client
    .fetch<HttpTypes.StoreCartResponse>(`/store/carts/${id}`, {
      method: "GET",
      query: {
        fields,
      },
      headers,
      next,
      cache: "force-cache",
    })
    .then(({ cart }: { cart: HttpTypes.StoreCart }) => cart)
    .catch(() => null)
}

/**
 * 登入中的會員在後端**已經不存在**時(測試機剛從正式站還原資料庫、或帳號被刪),
 * 後端對任何帶 token 的購物車請求都回 404「Customer with id … was not found」,
 * 前台就變成「無法加到購物車」且怎麼按都一樣(2026-10-08 dev 還原後實例)。
 * 這裡把它當成「登入已失效」處理:清掉 token、以訪客身分重做一次,顧客只要重新登入即可。
 */
const isStaleCustomer = (e: any) => /Customer with id: .* was not found/.test(String(e?.message ?? e))

async function withSessionFallback<T>(run: (headers: Record<string, string>) => Promise<T>): Promise<T> {
  const headers = { ...(await getAuthHeaders()) }
  try {
    return await run(headers)
  } catch (e: any) {
    if (!isStaleCustomer(e) || !("authorization" in headers)) throw e
    await removeAuthToken()
    return await run({})
  }
}

export async function getOrSetCart(countryCode: string) {
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  let cart = await retrieveCart(undefined, "id,region_id,completed_at")

  /**
   * 已結帳完成的購物車**仍然查得到**(GET 會成功),但不能再加東西 ——
   * 後端會回 400 `Cart is already completed`,前台就變 500。
   * 只要 cookie 還指著它(例如上一次結帳沒清乾淨),使用者會一直卡住,
   * 所以這裡主動丟掉、換一台新的。
   */
  if (cart?.completed_at) {
    await removeCartId()
    cart = null
  }

  if (!cart) {
    const locale = await getLocale()
    const cartResp = await withSessionFallback((headers) =>
      sdk.store.cart.create({ region_id: region.id, locale: locale || undefined }, {}, headers)
    )
    cart = cartResp.cart

    await setCartId(cart.id)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  if (cart && cart?.region_id !== region.id) {
    await withSessionFallback((headers) => sdk.store.cart.update(cart!.id, { region_id: region.id }, {}, headers))
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  return cart
}

export async function updateCart(data: HttpTypes.StoreUpdateCart) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found, please create one before updating")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .update(cartId, data, {}, headers)
    .then(async ({ cart }: { cart: HttpTypes.StoreCart }) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)

      return cart
    })
    .catch(medusaError)
}

/**
 * 對帳贈品:有觸發碼就把贈品加進購物車(0 元),碼沒了就拿掉。
 * 後端 `POST /store/carts/:id/gifts`(`backend/src/lib/cart-gifts.ts`)。
 *
 * 每一個會動到購物車的 action 結尾都要呼叫 —— 加 / 改 / 刪商品、套碼。
 * Medusa 沒有「促銷重算完」的 hook,所以靠前台這一步。
 *
 * 失敗只 log、回 null,**不擋原本的操作**:贈品沒加上可以下一次變動再補,
 * 商品加不進購物車不行。模組私有 —— 這個檔是 "use server",export 就變成公開 action。
 */
async function syncCartGifts(
  cartId: string,
  headers: Record<string, string>
): Promise<GiftSyncResult | null> {
  try {
    const { gifts } = await sdk.client.fetch<{ gifts: GiftSyncResult }>(
      `/store/carts/${cartId}/gifts`,
      { method: "POST", headers, cache: "no-store" }
    )

    if (gifts.added.length || gifts.removed.length) {
      revalidateTag(await getCacheTag("carts"))
      revalidateTag(await getCacheTag("fulfillment"))
    }

    return gifts
  } catch (e) {
    console.error("[cart] 同步贈品失敗(不擋購物車操作)", e)
    return null
  }
}

export async function addToCart({
  variantId,
  quantity,
  countryCode,
}: {
  variantId: string
  quantity: number
  countryCode: string
}) {
  if (!variantId) {
    throw new Error("Missing variant ID when adding to cart")
  }

  const cart = await getOrSetCart(countryCode)

  if (!cart) {
    throw new Error("Error retrieving or creating cart")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .createLineItem(
      cart.id,
      {
        variant_id: variantId,
        quantity,
      },
      {},
      headers
    )
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)

  // 付費品加進來後,原本「只剩贈品」而收回的贈品要補回來
  await syncCartGifts(cart.id, headers)

  /**
   * 推廣連結帶進來的折扣碼,在這裡自動套上。
   *
   * ⚠️ 要在 `createLineItem` **之後**,不能放進 `getOrSetCart` ——
   *    那個時間點購物車還是空的,「滿額才折」的規則一律不成立,
   *    我們會誤判成「這個碼死了」而把它清掉。
   *
   * 失敗不擋加入購物車:折扣沒套上可以事後補救,加不進購物車不行。
   */
  try {
    await applyPendingDiscount()
  } catch (e) {
    console.error("自動套用推廣折扣碼失敗", e)
  }
}

export async function updateLineItem({
  lineId,
  quantity,
}: {
  lineId: string
  quantity: number
}) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when updating line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when updating line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .updateLineItem(cartId, lineId, { quantity }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)

  await syncCartGifts(cartId, headers)
}

export async function deleteLineItem(lineId: string) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when deleting line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when deleting line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .deleteLineItem(cartId, lineId, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)

  // 刪掉付費品後只剩贈品 → 贈品也要收回
  await syncCartGifts(cartId, headers)
}

export async function setShippingMethod({
  cartId,
  shippingMethodId,
}: {
  cartId: string
  shippingMethodId: string
}) {
  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .addShippingMethod(cartId, { option_id: shippingMethodId }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
    })
    .catch(medusaError)
}

export async function initiatePaymentSession(
  cart: HttpTypes.StoreCart,
  data: HttpTypes.StoreInitializePaymentSession
) {
  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.payment
    .initiatePaymentSession(cart, data, {}, headers)
    .then(async (resp) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
      return resp
    })
    .catch(medusaError)
}

/**
 * 把會員上次沒結完的購物車接回瀏覽器。
 *
 * ## 為什麼需要這一支
 *
 * `signout()` 會 `removeCartId()` —— 那是刻意的:購物車轉移之後掛在會員身上,
 * 上面有 email 與收貨地址,不該留給下一個用同一台裝置的人。但 Medusa v2 的
 * Store API **沒有任何「用會員身分找購物車」的路徑**(`/store/carts` 列表回
 * 404,`/store/customers/me` 不帶 carts),所以 cookie 一刪,那台車就永遠找
 * 不回來了 —— 車還在資料庫、`customer_id` 也還在,只是沒有人指得到它。
 *
 * 結果是「登出等於永久放棄那台車」。這支配上後端的
 * `GET /store/account/active-cart` 補上回程。
 *
 * ## 🔴 呼叫端負責「有沒有東西會被蓋掉」
 *
 * 這支**無條件覆寫** `_medusa_cart_id`。目前唯一的呼叫點是 `transferCart()`,
 * 而它只在「沒有匿名車、或那台是空的」時才呼叫 —— 也就是沒有東西會被蓋掉的
 * 時候。要加新的呼叫點,先想清楚同一件事:顧客剛加進購物車的東西被一台舊車
 * 蓋掉,比「車沒回來」嚴重得多。
 *
 * @returns 接回來的 cart id;沒有可接的就回 null
 */
export async function restoreCustomerCart(): Promise<string | null> {
  const headers = {
    ...(await getAuthHeaders()),
  }

  // 沒登入就沒有「他的購物車」可言。後端也會 401,這裡先省一趟往返
  if (!("authorization" in headers)) {
    return null
  }

  const result = await sdk.client
    .fetch<{ cart_id: string | null }>("/store/account/active-cart", {
      method: "GET",
      headers,
      // 🔴 逐人不同的答案,不能進共用快取
      cache: "no-store",
    })
    .catch((e) => {
      // 接不回來不該擋住登入 —— 與 addToCart 裡自動套折扣碼同樣的取捨
      console.error("[cart] 找回會員上次的購物車失敗", e)
      return { cart_id: null }
    })

  if (!result?.cart_id) {
    return null
  }

  await setCartId(result.cart_id)

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  console.info(`[cart] 已接回會員上次的購物車 ${result.cart_id}`)

  return result.cart_id
}

export type ApplyPendingResult =
  | DiscountNotice
  | { status: "none" }
  /** 有碼但購物車還空著 —— 保留 cookie,等有商品再套 */
  | { status: "deferred"; code: string }

/**
 * 把推廣連結存下的折扣碼套到目前的購物車。
 *
 * ## 三個容易做錯的地方
 *
 * **1. 購物車空的時候不要套。** 空車會讓所有「滿額才折」的規則不成立,
 * 我們會誤判成「這個碼死了」並把它清掉 —— 顧客明明點了連結卻沒享到折扣。
 * 所以沒有品項時回 `deferred` 並**保留 cookie**。
 *
 * **2. `applyPromotions` 是整組覆寫。** 不先合併既有的碼,
 * 會把顧客手動輸入的折扣洗掉。
 *
 * **3. 🔴 套完一定要重讀確認,不能只靠 try/catch。**
 * Medusa 只有在「**完全查無此碼**」時才丟例外;碼存在但 `draft`/`inactive`、
 * 檔期未到或已過、**或規則不符(最常見:還沒到滿額門檻)**,都是**靜默丟棄**。
 * 只接例外的話,顧客會照原價下單、訂單上沒有那個碼的任何痕跡、事後查不出來,
 * 然後直播主來問「我的連結怎麼沒算到」。
 */
export async function applyPendingDiscount(): Promise<ApplyPendingResult> {
  const code = await getPendingDiscountCode()
  if (!code) return { status: "none" }

  const cart = await retrieveCart()
  if (!cart) return { status: "none" }

  // 1. 空車不套
  if (!cart.items?.length) return { status: "deferred", code }

  // 後台刪掉的促銷,購物車上的關聯不會跟著清,讀出來是 null(見 discount-code)
  const existing = (cart.promotions ?? [])
    .map((p: any) => p?.code)
    .filter(Boolean) as string[]

  // 已經在車上了 —— 可能是顧客自己輸入的,或上一輪套過了
  if (existing.includes(code)) {
    await clearPendingDiscountCode()
    return { status: "applied", code }
  }

  let notice: DiscountNotice

  try {
    // 2. 合併,不是覆寫
    const { cart: updated } = await applyPromotions([...existing, code])

    // 3. 重讀確認 —— 這一步才是真正判斷成敗的地方
    const landed = (updated?.promotions ?? []).some((p: any) => p?.code === code)

    notice = landed
      ? { status: "applied", code }
      : {
          status: "rejected",
          code,
          reason: "此折扣碼目前不適用(可能已過期、達使用上限,或不符使用條件)",
        }
  } catch (e: any) {
    // 走到這裡通常是「查無此碼」
    notice = { status: "rejected", code, reason: e?.message || "折扣碼無效" }
  }

  await clearPendingDiscountCode()
  await setDiscountNotice(notice)
  return notice
}

export async function applyPromotions(codes: string[]) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  /**
   * ⚠️ `promo_codes` 是**整組覆寫**,不是新增 —— 呼叫端必須自己先合併
   * `cart.promotions` 既有的碼,否則會把使用者手動輸入的折扣洗掉。
   *
   * 回傳 `{ cart, gifts }`。`cart` 是更新後的購物車(原本是丟掉)——
   * 自動套用需要它來**確認碼真的套上了**:碼存在但不適用時 Medusa 是靜默丟棄、
   * 不會丟例外,只接 catch 抓不到。store cart 路由的預設欄位已含 `promotions`,
   * 所以不必多打一次 API,也避開 `retrieveCart` 的 force-cache 讀到舊值。
   *
   * ⚠️ `cart` 是**對帳贈品之前**的快照 —— 碼的狀態是對的,品項不含剛加的贈品。
   *    要看品項的,走 `retrieveCart`(快取標籤已失效)。`gifts` 是對帳結果,
   *    贈品加不進去(沒庫存)時前台用它提示。
   */
  const cart = await sdk.store.cart
    .update(cartId, { promo_codes: codes }, {}, headers)
    .then(async ({ cart }) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)

      return cart
    })
    .catch(medusaError)

  const gifts = await syncCartGifts(cartId, headers)

  return { cart, gifts }
}

export async function applyGiftCard(code: string) {
  //   const cartId = getCartId()
  //   if (!cartId) return "No cartId cookie found"
  //   try {
  //     await updateCart(cartId, { gift_cards: [{ code }] }).then(() => {
  //       revalidateTag("cart")
  //     })
  //   } catch (error: any) {
  //     throw error
  //   }
}

export async function removeDiscount(code: string) {
  // const cartId = getCartId()
  // if (!cartId) return "No cartId cookie found"
  // try {
  //   await deleteDiscount(cartId, code)
  //   revalidateTag("cart")
  // } catch (error: any) {
  //   throw error
  // }
}

export async function removeGiftCard(
  codeToRemove: string,
  giftCards: any[]
  // giftCards: GiftCard[]
) {
  //   const cartId = getCartId()
  //   if (!cartId) return "No cartId cookie found"
  //   try {
  //     await updateCart(cartId, {
  //       gift_cards: [...giftCards]
  //         .filter((gc) => gc.code !== codeToRemove)
  //         .map((gc) => ({ code: gc.code })),
  //     }).then(() => {
  //       revalidateTag("cart")
  //     })
  //   } catch (error: any) {
  //     throw error
  //   }
}

export async function submitPromotionForm(
  currentState: unknown,
  formData: FormData
) {
  const code = formData.get("code") as string
  try {
    await applyPromotions([code])
  } catch (e: any) {
    return e.message
  }
}

// TODO: Pass a POJO instead of a form entity here
export async function setAddresses(currentState: unknown, formData: FormData) {
  try {
    if (!formData) {
      throw new Error("No form data found when setting addresses")
    }
    const cartId = getCartId()
    if (!cartId) {
      throw new Error("No existing cart found when setting addresses")
    }

    const data = {
      shipping_address: {
        first_name: formData.get("shipping_address.first_name"),
        last_name: formData.get("shipping_address.last_name"),
        address_1: formData.get("shipping_address.address_1"),
        address_2: formData.get("shipping_address.address_2") || "",
        company: formData.get("shipping_address.company"),
        postal_code: formData.get("shipping_address.postal_code"),
        city: formData.get("shipping_address.city"),
        country_code: formData.get("shipping_address.country_code"),
        province: formData.get("shipping_address.province"),
        phone: formData.get("shipping_address.phone"),
      },
    // ⚠️ 空字串一定要轉成 null。email 現在是選填,而後端的 zod 是
    //    `.email().nullish()` —— 放行 null,但空字串不是合法 email,會 400。
    email: (formData.get("email") as string)?.trim() || null,
    } as any

    const sameAsBilling = formData.get("same_as_billing")
    if (sameAsBilling === "on") data.billing_address = data.shipping_address

    if (sameAsBilling !== "on")
      data.billing_address = {
        first_name: formData.get("billing_address.first_name"),
        last_name: formData.get("billing_address.last_name"),
        address_1: formData.get("billing_address.address_1"),
        address_2: "",
        company: formData.get("billing_address.company"),
        postal_code: formData.get("billing_address.postal_code"),
        city: formData.get("billing_address.city"),
        country_code: formData.get("billing_address.country_code"),
        province: formData.get("billing_address.province"),
        phone: formData.get("billing_address.phone"),
      }
    await updateCart(data)
  } catch (e: any) {
    return e.message
  }

  redirect(
    `/${formData.get("shipping_address.country_code")}/checkout?step=delivery`
  )
}

/**
 * 送單前:購物車有選過全家門市(cart.metadata.family_store_*),但最後選的運送方式不是全家店到店
 * (顧客選完門市又換成宅配),把門市欄位剝掉,不要殘留到 order.metadata。
 * 配送步驟換方式時**不清**門市(2026-10-13 使用者:換去宅配再換回全家,卡片不見了),所以在這裡收尾。
 * 全家的判斷與配送步驟一致:shipping option type `family` / `family-cod`,或名稱含「全家」。失敗不擋送單。
 */
async function stripFamilyStoreIfNotFamily(cartId: string, headers: Record<string, string>) {
  try {
    const { cart } = await sdk.store.cart.retrieve(
      cartId,
      { fields: "metadata,*shipping_methods" },
      headers
    )
    const meta = (cart?.metadata ?? {}) as Record<string, unknown>
    const familyKeys = Object.keys(meta).filter((k) => k.startsWith("family_store_") && meta[k] != null)
    if (!familyKeys.length) return
    const methods = cart?.shipping_methods ?? []
    if (!methods.length) return
    const { shipping_options } = await sdk.store.fulfillment.listCartOptions({ cart_id: cartId }, headers)
    const isFamily = methods.some((m) => {
      const o = shipping_options?.find((so) => so.id === m.shipping_option_id)
      const code = (o as any)?.type?.code
      return code === "family" || code === "family-cod" || !!(o?.name ?? m.name)?.includes("全家")
    })
    if (isFamily) return
    await sdk.store.cart.update(
      cartId,
      { metadata: { ...meta, ...Object.fromEntries(familyKeys.map((k) => [k, null])) } },
      {},
      headers
    )
  } catch (e) {
    console.log("[placeOrder] 剝除殘留的全家門市失敗(不擋送單)", (e as any)?.message ?? e)
  }
}

/**
 * Places an order for a cart. If no cart ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to place an order for.
 * @returns The cart object if the order was successful, or null if not.
 */
export async function placeOrder(cartId?: string) {
  const id = cartId || (await getCartId())

  if (!id) {
    throw new Error("No existing cart found when placing an order")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await stripFamilyStoreIfNotFamily(id, headers)

  const cartRes = await sdk.store.cart
    // ⚠️ 要明確加 customer_id。complete 的預設欄位有 47 個、含 shipping_address,
    //    唯獨**沒有 customer_id** —— 少了它,saveOrderShippingAddressToAccount()
    //    會在第一行就 return,而且是在 try/catch 之前,連錯誤都不會留。
    .complete(id, { fields: "+customer_id" }, headers)
    .then(async (cartRes) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
      return cartRes
    })
    .catch(medusaError)

  if (cartRes?.type === "order") {
    const countryCode =
      cartRes.order.shipping_address?.country_code?.toLowerCase()

    const orderCacheTag = await getCacheTag("orders")
    revalidateTag(orderCacheTag)

    // 結帳填的地址複製一份進會員地址簿。務必在 redirect 之前 ——
    // redirect() 會 throw NEXT_REDIRECT,放後面永遠不會執行。
    await saveOrderShippingAddressToAccount(cartRes.order)
    // 全家取貨門市也記到會員身上,下次結帳選全家直接代入(2026-10-13)
    await saveFamilyStoreToAccount(cartRes.order)

    // ⚠️ 一定要 await:removeCartId 是 async,而下一行 redirect() 靠 throw 實作 ——
    //    不等它完成,cookie 常常沒真的刪掉,使用者就會抱著一個「已完成」的購物車,
    //    之後每次加入商品都被後端擋(Cart is already completed → 前台 500)。
    await removeCartId()
    redirect(`/${countryCode}/order/${cartRes?.order.id}/confirmed`)
  }

  return cartRes.cart
}

/**
 * Updates the countrycode param and revalidates the regions cache
 * @param regionId
 * @param countryCode
 */
export async function updateRegion(countryCode: string, currentPath: string) {
  const cartId = await getCartId()
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  if (cartId) {
    await updateCart({ region_id: region.id })
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  const regionCacheTag = await getCacheTag("regions")
  revalidateTag(regionCacheTag)

  const productsCacheTag = await getCacheTag("products")
  revalidateTag(productsCacheTag)

  redirect(`/${countryCode}${currentPath}`)
}

export async function listCartOptions() {
  const cartId = await getCartId()
  const headers = {
    ...(await getAuthHeaders()),
  }
  const next = {
    ...(await getCacheOptions("shippingOptions")),
  }

  return await sdk.client.fetch<{
    shipping_options: HttpTypes.StoreCartShippingOption[]
  }>("/store/shipping-options", {
    query: { cart_id: cartId },
    next,
    headers,
    cache: "force-cache",
  })
}
